import React, { Fragment, useCallback, useMemo } from 'react';
import cx from 'classnames';

import { ZOLA_DOMAIN } from '@zola-helpers/client/dist/es/constants/url';
import { CaretV2Icon } from '../SvgIcons/CaretV2';
import { LinkV2 } from '../LinkV2';

import styles from './TotalItemPagination.module.less';

export type TotalItemPaginationProps = {
  className?: string;
  /** Number of current page */
  currentPage: number;
  /** Current URL path */
  currentUrl: string;
  /** Caps number of pages to show */
  maxPageCountToShow?: number;
  /** Can use to override href behavior of pagination links */
  onClick?: (event: React.MouseEvent, page: number) => void;
  /** How many items to show on each page */
  pageSize?: number;
  /** Total number of items */
  totalItems: number;
};

type BasePageButtonProps = {
  currentUrl: string;
  onClick: (event: React.MouseEvent, page: number) => void;
  page: number;
};

type NumberPageButtonProps = {
  isCurrentPage?: boolean;
} & BasePageButtonProps;

type ArrowPageButtonProps = {
  disabled: boolean;
  isNext?: boolean;
} & BasePageButtonProps;

const constructHref = (currentUrl: string, page: number) => {
  const url = new URL(`${ZOLA_DOMAIN}${currentUrl}`);
  const params = new URLSearchParams(url.search);
  params.set('page', page.toString());
  return `${url.pathname}?${params.toString()}`;
};

const NumberPageButton = ({
  currentUrl,
  isCurrentPage = false,
  onClick,
  page,
}: NumberPageButtonProps) => {
  return (
    <LinkV2
      aria-label={`Go to page ${page}`}
      className={cx(styles.numberPageButton, { [styles.currentPage]: isCurrentPage })}
      href={constructHref(currentUrl, page)}
      onClick={(event) => onClick(event, page)}
    >
      {page}
    </LinkV2>
  );
};

const ArrowPageButton = ({
  currentUrl,
  disabled,
  onClick,
  page,
  isNext = false,
}: ArrowPageButtonProps): JSX.Element => {
  return (
    <div className={styles.arrowPageButton}>
      <LinkV2
        aria-label={`Go to ${isNext ? 'next' : 'previous'} page`}
        className={cx({ [styles.disabled]: disabled })}
        disabled={disabled}
        href={constructHref(currentUrl, page)}
        noUnderline
        onClick={(event) => onClick(event, page)}
      >
        {isNext ? (
          <>
            Next <CaretV2Icon aria-hidden direction="right" />
          </>
        ) : (
          <>
            <CaretV2Icon aria-hidden direction="left" /> Back
          </>
        )}
      </LinkV2>
    </div>
  );
};

const PreviousPagesSkippedIndicator = () => <span data-testid="fewer-pages-indicator">...</span>;
const UpcomingPagesSkippedIndicator = () => <span data-testid="more-pages-indicator">...</span>;

// This might work if updated to 7, but its almost guaranteed to fail
// for even numbers and anything less than 3 as we need a middle point.
const DESKTOP_PAGES_TO_SHOW = 5;
const MOBILE_PAGES_TO_SHOW = 3;

/**
 * Pagination control when the number of total items is known.
 *
 * Rules for desktop:
 * - Page number 1 and last page number must always be visible
 * - The next and previous number must be visible if available.  For example, you must be able to click page 3, 4, or 5 when on page 4
 * - We will show at most DESKTOP_PAGES_TO_SHOW page numbers at a time
 * - We will show skip indicators "..." if there are more pages to show
 *
 * Rules for mobile:
 * - If the previous button is enabled, show "..."
 * - Show the current page and next/previous numbers if available
 * - If the next button is enabled, show "..."
 */
const TotalItemPagination = ({
  className,
  currentPage,
  currentUrl,
  maxPageCountToShow,
  onClick = () => undefined,
  pageSize = 60,
  totalItems,
}: TotalItemPaginationProps): JSX.Element => {
  // How many items are there total
  const totalPageCount = useMemo(() => {
    let pageCount = Math.ceil(totalItems / pageSize);
    if (maxPageCountToShow && maxPageCountToShow < pageCount) {
      pageCount = maxPageCountToShow;
    }
    return pageCount;
  }, [maxPageCountToShow, pageSize, totalItems]);

  const hasNextButton = currentPage < totalPageCount;
  const hasBackButton = currentPage > 1;

  const renderMobileSlots = useCallback(() => {
    const slots = [];
    if (totalPageCount <= MOBILE_PAGES_TO_SHOW) {
      // Show as many items as we have pages "1  2  3"
      for (let i = 1; i <= totalPageCount; i += 1) {
        slots.push(
          <NumberPageButton
            currentUrl={currentUrl}
            isCurrentPage={currentPage === i}
            key={`page-button-${i}`}
            onClick={onClick}
            page={i}
          />
        );
      }
    } else if (currentPage < MOBILE_PAGES_TO_SHOW) {
      // "1  2  3  ..."
      for (let i = 1; i <= MOBILE_PAGES_TO_SHOW; i += 1) {
        slots.push(
          <NumberPageButton
            currentUrl={currentUrl}
            isCurrentPage={currentPage === i}
            key={`page-button-${i}`}
            onClick={onClick}
            page={i}
          />
        );
      }
      slots.push(<UpcomingPagesSkippedIndicator key="more-pages-indicator" />);
    } else if (currentPage > totalPageCount - 2) {
      //  "...  18  19  20"
      slots.push(<PreviousPagesSkippedIndicator key="previous-pages-indicator" />);
      for (let i = MOBILE_PAGES_TO_SHOW - 1; i >= 0; i -= 1) {
        slots.push(
          <NumberPageButton
            currentUrl={currentUrl}
            isCurrentPage={currentPage === totalPageCount - i}
            key={`page-button-${totalPageCount - i}`}
            onClick={onClick}
            page={totalPageCount - i}
          />
        );
      }
    } else {
      // "...  n-1  n  n+1  ..."
      slots.push(<PreviousPagesSkippedIndicator key="previous-pages-indicator" />);
      for (let i = currentPage - 1; i <= currentPage + 1; i += 1) {
        slots.push(
          <NumberPageButton
            currentUrl={currentUrl}
            isCurrentPage={currentPage === i}
            key={`page-button-${i}`}
            onClick={onClick}
            page={i}
          />
        );
      }
      slots.push(<UpcomingPagesSkippedIndicator key="more-pages-indicator" />);
    }
    return <Fragment>{slots}</Fragment>;
  }, [totalPageCount, currentPage, currentUrl, onClick]);

  const renderDesktopSlots = useCallback(() => {
    const slots = [];
    if (totalPageCount <= DESKTOP_PAGES_TO_SHOW) {
      // Show as many items as we have pages "1  2  3  4  5"
      for (let i = 1; i < totalPageCount; i += 1) {
        slots.push(
          <NumberPageButton
            currentUrl={currentUrl}
            isCurrentPage={currentPage === i}
            key={`page-button-${i}`}
            onClick={onClick}
            page={i}
          />
        );
      }
    } else {
      // Always show page 1 on desktop
      slots.push(
        <NumberPageButton
          currentUrl={currentUrl}
          isCurrentPage={currentPage === 1}
          key="page-button-1"
          onClick={onClick}
          page={1}
        />
      );
      if (currentPage < DESKTOP_PAGES_TO_SHOW - 1) {
        // Within the first 3, so we want to show "1  2  3  4  ... n"
        for (let i = 2; i < DESKTOP_PAGES_TO_SHOW; i += 1) {
          slots.push(
            <NumberPageButton
              currentUrl={currentUrl}
              isCurrentPage={currentPage === i}
              key={`page-button-${i}`}
              onClick={onClick}
              page={i}
            />
          );
        }
        slots.push(<UpcomingPagesSkippedIndicator key="more-pages-indicator" />);
      } else if (currentPage > totalPageCount - 3) {
        // Within the last 3, (on page 18-20 if there are 20 pages or page 4-6 if there are 6 pages)
        // "1  ...  17  18  19  20"
        const pageNumbersToShow = [];
        for (let i = DESKTOP_PAGES_TO_SHOW - 2; i > 0; i -= 1) {
          pageNumbersToShow.push(totalPageCount - i);
        }
        slots.push(<PreviousPagesSkippedIndicator key="previous-pages-indicator" />);
        pageNumbersToShow.forEach((i) => {
          slots.push(
            <NumberPageButton
              currentUrl={currentUrl}
              isCurrentPage={currentPage === i}
              key={`page-button-${i}`}
              onClick={onClick}
              page={i}
            />
          );
        });
      } else {
        // We aren't in the first few pages, and we aren't in the last few
        // "1  ...  n-1  n  n+1  ...  n"
        slots.push(<PreviousPagesSkippedIndicator key="previous-pages-indicator" />);
        for (let i = currentPage - 1; i <= currentPage + 1; i += 1) {
          slots.push(
            <NumberPageButton
              currentUrl={currentUrl}
              isCurrentPage={currentPage === i}
              key={`page-button-${i}`}
              onClick={onClick}
              page={i}
            />
          );
        }
        slots.push(<UpcomingPagesSkippedIndicator key="more-pages-indicator" />);
      }
    }
    // Always show last page number on desktop
    slots.push(
      <NumberPageButton
        currentUrl={currentUrl}
        isCurrentPage={currentPage === totalPageCount}
        key={`page-button-${totalPageCount}`}
        onClick={onClick}
        page={totalPageCount}
      />
    );
    return <Fragment>{slots}</Fragment>;
  }, [totalPageCount, currentUrl, currentPage, onClick]);

  return (
    <div className={cx(styles.totalItemPagination, className)}>
      <div className={styles.totalItemPaginationInner}>
        <ArrowPageButton
          currentUrl={currentUrl}
          disabled={!hasBackButton}
          onClick={onClick}
          page={Math.max(1, currentPage - 1)}
        />
        <div className={styles.hideOnTabletDesktop} data-testid="pagination-controls-mobile">
          {renderMobileSlots()}
        </div>
        <div className={styles.hideOnMobile} data-testid="pagination-controls-desktop">
          {renderDesktopSlots()}
        </div>
        <ArrowPageButton
          currentUrl={currentUrl}
          disabled={!hasNextButton}
          onClick={onClick}
          page={Math.min(currentPage + 1, totalPageCount)}
          isNext
        />
      </div>
    </div>
  );
};

export default TotalItemPagination;
